import type { UseFormReturnType } from "@mantine/form";

import { Flex, InputLabel, ScrollArea, Text, TextInput } from "@mantine/core";

import type { CollectCustomerInfoFormData } from "@/sales-tools/home-product/hooks/useCollectCustomerInfoForm";

import { AutoAddressInput } from "@/sales-tools/home-product/components/CollectCustomerInfo/AutoAddressInput";
import { InfoIcon } from "@/sales-tools/home-product/components/CollectCustomerInfo/InfoIcon";
import { ManualAddressInput } from "@/sales-tools/home-product/components/CollectCustomerInfo/ManualAddressInput";

import classes from "./CollectCustomerInfo.module.css";

export interface CollectCustomerInfoFormProps {
    form: UseFormReturnType<CollectCustomerInfoFormData>;
    isAddressEligible: boolean;
    isLoading: boolean;
    addressSuggestions: { value: string; label: string }[];
    phoneNumber: string;
    showAddressNotFoundMessage: boolean;
    onAddAddressManually: () => void;
    showManualAddressInput: boolean;
}

export const CollectCustomerInfoForm = ({
    form,
    isAddressEligible,
    isLoading,
    addressSuggestions,
    phoneNumber,
    showAddressNotFoundMessage,
    onAddAddressManually,
    showManualAddressInput,
}: CollectCustomerInfoFormProps) => {
    return (
        <Flex w="100%" direction="column" justify={"space-between"}>
            <ScrollArea className={classes.scrollArea}>
                {!isAddressEligible && (
                    <Flex className={`${classes.warning} + ${classes.fade}`} gap="sm">
                        <InfoIcon width={16} height={16} className={classes.fade} />
                        <Text fz="xs" className={classes.fade}>
                            The provided address was ineligible. Please verify the home address and try again.
                        </Text>
                    </Flex>
                )}

                <Flex direction={"column"} p="xl" className={isAddressEligible ? undefined : classes.animationSpacer}>
                    <Text fz="h5" fw="700">
                        Collect info
                    </Text>

                    <Flex gap="1rem" justify="space-between">
                        <Flex direction="column" w="100%">
                            <InputLabel size="xs">First name</InputLabel>
                            <TextInput
                                aria-label="First name"
                                className="fs-mask"
                                mt="3xs"
                                id="customer-first-name-input"
                                size="sm"
                                classNames={{ input: classes.styledInput }}
                                {...form.getInputProps("firstName")}
                            />
                        </Flex>

                        <Flex direction="column" w="100%">
                            <InputLabel size="xs">Last name</InputLabel>
                            <TextInput
                                aria-label="Last name"
                                className="fs-mask"
                                mt="3xs"
                                id="customer-last-name-input"
                                size="sm"
                                classNames={{ input: classes.styledInput }}
                                {...form.getInputProps("lastName")}
                            />
                        </Flex>
                    </Flex>

                    <Flex direction="column" mt="xl">
                        <InputLabel size="xs">Phone number</InputLabel>
                        <TextInput
                            className="fs-mask"
                            mt="3xs"
                            id="customer-phone-number-input"
                            size="sm"
                            fz="xs"
                            classNames={{ input: classes.disabledInput }}
                            defaultValue={phoneNumber}
                            readOnly
                        />
                    </Flex>

                    <Flex direction="column" mt="xl">
                        <InputLabel size="xs">Email</InputLabel>
                        <TextInput
                            aria-label="Email"
                            className="fs-mask"
                            mt="3xs"
                            id="customer-email-input"
                            size="sm"
                            classNames={{ input: classes.styledInput }}
                            {...form.getInputProps("email")}
                        />
                    </Flex>

                    <Flex direction="column" mt="xl">
                        {showManualAddressInput ? (
                            <ManualAddressInput form={form} />
                        ) : (
                            <AutoAddressInput
                                form={form}
                                isLoading={isLoading}
                                addressSuggestions={addressSuggestions}
                                showAddressNotFoundMessage={showAddressNotFoundMessage}
                                onAddAddressManually={onAddAddressManually}
                            />
                        )}
                    </Flex>
                </Flex>
            </ScrollArea>
        </Flex>
    );
};
